import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Pencil, Trash2, Check, X, Download, RefreshCw, Search,
  ArrowUpDown, ArrowUp, ArrowDown, Eraser, Trophy, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { flagUrl, nationalityLabel, NATIONALITIES } from "@/lib/nationalities";
import EliminatoriasUserDialog from "./EliminatoriasUserDialog";

/**
 * Admin · Eliminatórias Escolares
 * Manages self-registered users that joined the knockout phase.
 */
export default function AdminEliminatoriasUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [editing, setEditing] = useState(null); // { id, name, school, nationality }
  const [historyUserId, setHistoryUserId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (schoolFilter.trim()) params.school = schoolFilter.trim();
      const { data } = await api.get("/eliminatorias/users", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar utilizadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-apply school filter when it changes (no need to click "Procurar").
  // Debounce the free-text search box so we don't spam the server while typing.
  useEffect(() => {
    if (!schoolFilter) return; // initial mount handled above
    load();
  }, [schoolFilter]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // List of schools for the filter dropdown.
  const schools = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.school && s.add(r.school));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const sorted = useMemo(() => {
    const out = [...rows];
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === "passed_count") {
        va = (a.passed_eliminatoria_ids || []).length;
        vb = (b.passed_eliminatoria_ids || []).length;
      } else if (sortKey === "finalist_count") {
        va = (a.finalist_games || []).length;
        vb = (b.finalist_games || []).length;
      }
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" || key === "last_seen_at" ? "desc" : "asc");
    }
  };

  const sortIcon = (k) => <SortIconBase active={sortKey === k} dir={sortDir} />;

  const startEdit = (r) => {
    setEditing({
      id: r.id,
      name: r.name || "",
      school: r.school || "",
      nationality: r.nationality || "PT",
    });
  };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      const { id, ...payload } = editing;
      await api.put(`/eliminatorias/users/${id}`, {
        name: payload.name || null,
        school: payload.school || null,
        nationality: payload.nationality || null,
      });
      toast.success("Utilizador atualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    }
  };

  const removeOne = async (id, label) => {
    if (!window.confirm(`Apagar "${label}" e todas as suas tentativas?`)) return;
    await api.delete(`/eliminatorias/users/${id}`);
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    toast.success("Apagado");
    load();
  };

  const clearData = async (id, label) => {
    if (!window.confirm(`Limpar todas as tentativas e progresso de "${label}"?\n(A conta é mantida.)`)) return;
    await api.post(`/eliminatorias/users/${id}/clear-data`, {});
    toast.success("Progresso limpo");
    load();
  };

  const toggleSelect = (id) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    const visible = sorted.map((r) => r.id);
    const all = visible.length > 0 && visible.every((id) => selected.has(id));
    setSelected((s) => {
      const n = new Set(s);
      if (all) visible.forEach((id) => n.delete(id));
      else visible.forEach((id) => n.add(id));
      return n;
    });
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    try {
      const { data } = await api.post("/eliminatorias/users/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} apagado(s)`);
      setSelected(new Set());
      setConfirmBulk(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (schoolFilter.trim()) params.set("school", schoolFilter.trim());
      const url = `/eliminatorias/users.xlsx${params.toString() ? `?${params}` : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `eliminatorias_users_${new Date().toISOString().slice(0, 10)}.xlsx`;
      dl.click();
      URL.revokeObjectURL(dl.href);
      toast.success("Excel descarregado");
    } catch (e) {
      toast.error("Falha ao exportar");
    }
  };

  return (
    <div data-testid="admin-elim-users">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-violet-600 text-white p-2.5 shadow-md">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900 leading-tight">Eliminatórias Escolares</h1>
            <p className="text-sm text-slate-500">Utilizadores self-service inscritos na fase de eliminatórias.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" className="rounded-full" data-testid="elim-users-reload">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Recarregar
          </Button>
          <Button onClick={exportExcel} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="elim-users-export">
            <Download className="w-4 h-4 mr-1.5" /> Exportar Excel
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label>Pesquisa (username / email / nome / escola)</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                data-testid="elim-users-search"
                className="pl-9"
                placeholder="ex: ana, escola exemplo, gmail.com"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>
          </div>
          <div>
            <Label>Filtrar por escola</Label>
            <Select value={schoolFilter || "__all__"} onValueChange={(v) => setSchoolFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="elim-users-school-filter"><SelectValue placeholder="Todas as escolas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as escolas</SelectItem>
                {schools.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={load} variant="outline" data-testid="elim-users-search-btn" className="rounded-full">
              <Search className="w-4 h-4 mr-1.5" /> Procurar
            </Button>
            <Button
              onClick={() => setConfirmBulk(true)}
              disabled={!selected.size}
              variant="outline"
              className="border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50 rounded-full"
              data-testid="elim-users-delete-selected"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Apagar ({selected.size})
            </Button>
          </div>
        </div>
      </div>

      {confirmBulk && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="elim-users-confirm-bulk">
          <div className="text-sm text-rose-800">
            Confirmar apagar <b>{selected.size}</b> utilizador(es) e todas as suas tentativas? Esta ação não pode ser desfeita.
          </div>
          <div className="flex gap-2">
            <Button onClick={bulkDelete} className="bg-rose-600 text-white" data-testid="elim-users-confirm-bulk-yes">Sim, apagar</Button>
            <Button onClick={() => setConfirmBulk(false)} variant="outline">Cancelar</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  data-testid="elim-users-select-all"
                  className="w-4 h-4 cursor-pointer"
                  checked={sorted.length > 0 && sorted.every((r) => selected.has(r.id))}
                  onChange={toggleSelectAll}
                />
              </th>
              <Th onClick={() => toggleSort("username")} active={sortKey === "username"} dir={sortDir} testid="th-username">
                Username {sortIcon("username")}
              </Th>
              <Th onClick={() => toggleSort("email")} active={sortKey === "email"} dir={sortDir} testid="th-email">
                Email {sortIcon("email")}
              </Th>
              <Th onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir} testid="th-name">
                Nome {sortIcon("name")}
              </Th>
              <Th onClick={() => toggleSort("school")} active={sortKey === "school"} dir={sortDir} testid="th-school">
                Escola {sortIcon("school")}
              </Th>
              <Th onClick={() => toggleSort("nationality")} active={sortKey === "nationality"} dir={sortDir} testid="th-nat">
                País {sortIcon("nationality")}
              </Th>
              <Th onClick={() => toggleSort("passed_count")} active={sortKey === "passed_count"} dir={sortDir} testid="th-passed" center>
                Passadas {sortIcon("passed_count")}
              </Th>
              <Th onClick={() => toggleSort("finalist_count")} active={sortKey === "finalist_count"} dir={sortDir} testid="th-finalist" center>
                Finalista {sortIcon("finalist_count")}
              </Th>
              <Th onClick={() => toggleSort("created_at")} active={sortKey === "created_at"} dir={sortDir} testid="th-created">
                Criado {sortIcon("created_at")}
              </Th>
              <th className="text-left px-4 py-3 font-bold text-slate-700 uppercase text-xs tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="elim-users-table">
            {sorted.map((r) => {
              const isEditing = editing?.id === r.id;
              const isSel = selected.has(r.id);
              const passedCount = (r.passed_eliminatoria_ids || []).length;
              const finalistCount = (r.finalist_games || []).length;
              return (
                <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-violet-50" : ""}`} data-testid={`elim-user-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`elim-user-select-${r.id}`}
                      className="w-4 h-4 cursor-pointer"
                      checked={isSel}
                      onChange={() => toggleSelect(r.id)}
                    />
                  </td>
                  <td className="px-4 py-2 text-slate-700 font-semibold">{r.username}</td>
                  <td className="px-4 py-2 text-slate-700">{r.email}</td>
                  <td className="px-4 py-2 text-slate-700">
                    {isEditing ? (
                      <Input
                        autoFocus
                        value={editing.name}
                        onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                        data-testid={`elim-edit-name-${r.id}`}
                      />
                    ) : (
                      r.name || <em className="text-slate-400">—</em>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {isEditing ? (
                      <Input
                        value={editing.school}
                        onChange={(e) => setEditing((p) => ({ ...p, school: e.target.value }))}
                        data-testid={`elim-edit-school-${r.id}`}
                      />
                    ) : (
                      r.school || <em className="text-slate-400">—</em>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {isEditing ? (
                      <Select value={editing.nationality} onValueChange={(v) => setEditing((p) => ({ ...p, nationality: v }))}>
                        <SelectTrigger data-testid={`elim-edit-nat-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {NATIONALITIES.map((n) => <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {flagUrl(r.nationality) && <img src={flagUrl(r.nationality)} alt={r.nationality} className="w-4 h-3 object-cover rounded-sm" />}
                        <span>{nationalityLabel(r.nationality) || r.nationality || "-"}</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-xs font-bold bg-violet-100 text-violet-700 ring-1 ring-violet-300" data-testid={`elim-passed-${r.id}`}>
                      {passedCount}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {finalistCount > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-xs font-bold bg-amber-100 text-amber-800 ring-1 ring-amber-300" data-testid={`elim-finalist-${r.id}`}>
                        <Trophy className="w-3 h-3" /> {finalistCount}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "-"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-700 p-1" data-testid={`elim-save-${r.id}`} title="Guardar"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-700 p-1"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setHistoryUserId(r.id)} className="text-sky-600 hover:text-sky-700 p-1" data-testid={`elim-edit-${r.id}`} title="Editar / ver histórico"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => clearData(r.id, r.username)} className="text-amber-600 hover:text-amber-700 p-1" data-testid={`elim-clear-${r.id}`} title="Limpar progresso"><Eraser className="w-4 h-4" /></button>
                          <button onClick={() => removeOne(r.id, r.username)} className="text-rose-600 hover:text-rose-700 p-1" data-testid={`elim-delete-${r.id}`} title="Apagar"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!sorted.length && !loading && (
              <tr>
                <td colSpan={10} className="text-center py-12 text-slate-400" data-testid="elim-users-empty">
                  {search || schoolFilter ? "Sem utilizadores correspondentes aos filtros." : "Ainda não há inscrições nas eliminatórias."}
                </td>
              </tr>
            )}
            {loading && (
              <tr><td colSpan={10} className="text-center py-12 text-slate-400">A carregar…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
        <span>{sorted.length} utilizador(es) {(search || schoolFilter) && "(filtrados)"}</span>
        <span>Ordenação: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{sortKey} {sortDir}</code></span>
      </div>

      {historyUserId && (
        <EliminatoriasUserDialog
          userId={historyUserId}
          onClose={() => setHistoryUserId(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}

function Th({ children, onClick, active, dir, testid, center }) {
  return (
    <th
      onClick={onClick}
      data-testid={testid}
      className={`text-${center ? "center" : "left"} px-4 py-3 font-bold uppercase text-xs tracking-wider cursor-pointer select-none ${
        active ? "text-violet-700" : "text-slate-700"
      } hover:text-violet-700 transition-colors`}
    >
      {children}
    </th>
  );
}

function SortIconBase({ active, dir }) {
  if (!active) return <ArrowUpDown className="inline w-3 h-3 ml-1 opacity-40" />;
  return dir === "asc"
    ? <ArrowUp className="inline w-3 h-3 ml-1 text-violet-600" />
    : <ArrowDown className="inline w-3 h-3 ml-1 text-violet-600" />;
}
